/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: ImovelPersistenceService
 * VERSÃO:  2.1.0
 * ------------------------------------------------------------
 * MUDANÇA v2.1.0:
 *   ✔ Aceita resposta com `ok` OU `success` (compatibilidade)
 *   ✔ Propaga `_etapa_atual` recebido do backend
 *   ✔ Loga payload em dev para debug
 *
 * v2.0.0:
 *   ✔ Envia payload PLANO (não envelopado) — backend normaliza
 *   ✔ Guarda o UUID retornado pelo backend no formData
 * ============================================================
 */

import axios from 'axios';

/* ============================================================
   Normaliza entrada — aceita 1 ou 3 argumentos
   ============================================================ */

function normalizarEntrada(arg1, arg2, arg3) {
  // Formato antigo: (imovel)
  if (arg1 && (arg1.laudo_id || arg1.laudoRef) && arg2 === undefined) {
    return { imovel: arg1 };
  }

  // Formato novo: (requerente, dadosImovel, opts)
  const requerente  = arg1 || {};
  const dadosImovel = arg2 || {};
  const opts        = arg3 || {};

  const codigoLaudo = opts.laudoId
    || dadosImovel.laudo_id
    || dadosImovel.laudoRef;

  if (!codigoLaudo) {
    return { erro: 'laudoId não informado.' };
  }

  const imovel = {
    id:        dadosImovel.id || undefined,
    laudo_id:  codigoLaudo,

    // Requerente (compatível com wizard)
    requerente: {
      nome:              requerente.nome              || '',
      documento:         requerente.documento         || '',
      processoJudicial:  requerente.processoJudicial  || '',
      finalidade_codigo: requerente.finalidade_codigo || '',
      finalidade_nome:   requerente.finalidade_nome   || '',
      comarca_nome:      requerente.comarca_nome      || '',
      forum:             requerente.forum             || '',
      vara:              requerente.vara              || '',
      objetivo:          requerente.objetivo          || '',
      seloIntegridade:   requerente.seloIntegridade   || '',
    },

    // Endereço
    logradouro:  dadosImovel.logradouro  || '',
    numero:      dadosImovel.numero      || '',
    complemento: dadosImovel.complemento || '',
    bairro:      dadosImovel.bairro      || '',
    cidade:      dadosImovel.cidade      || '',
    uf:          dadosImovel.uf          || '',
    cep:         dadosImovel.cep         || '',

    // Coordenadas
    lat: dadosImovel.lat || '',
    lng: dadosImovel.lng || '',
    geocodificacao: dadosImovel.geocodificacao || null,

    // Dimensões
    areaTerreno: dadosImovel.areaTerreno || '',
    frente:      dadosImovel.frente      || '',

    // Registral (RGI / cartográfico)
    matricula:    dadosImovel.matricula    || '',
    rgi:          dadosImovel.rgi          || dadosImovel.matricula || '',
    cartografico: dadosImovel.cartografico || '',

    // Caracterização
    uso:               dadosImovel.uso              || '',
    tipologia:         dadosImovel.tipologia        || '',
    padrao:            dadosImovel.padrao           || '',
    idadeReal:         dadosImovel.idadeReal        || '',
    estadoConservacao: dadosImovel.estadoConservacao|| 'c',
    vidaUtilIBAPE:     dadosImovel.vidaUtilIBAPE    || null,
    contextoReforma:   dadosImovel.contextoReforma  || null,

    topografia:      dadosImovel.topografia      || null,
    fatorTopografia: dadosImovel.fatorTopografia || null,

    documentos:      dadosImovel.documentos      || {},
    dadosMatricula:  dadosImovel.dadosMatricula  || null,
    estadoMatricula: dadosImovel.estadoMatricula || null,
    logAgendamento:  dadosImovel.logAgendamento  || [],

    // ⚠️ CRÍTICO: persistir etapa atual para retomar no F5
    _etapa_atual: dadosImovel._etapa_atual || null,
  };

  return { imovel };
}

/* ============================================================
   API PÚBLICA
   ============================================================ */

export const salvarEstadoSoberano = async (arg1, arg2, arg3) => {
  const { imovel, erro } = normalizarEntrada(arg1, arg2, arg3);

  if (erro) {
    console.warn('[ImovelPersistenceService] Payload inválido:', erro);
    return { success: false, error: erro };
  }

  try {
    const resp = await axios.post('/api/imovel/salvar.php', { imovel }, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });

    const data = resp.data || {};

    // Compatibilidade: backend pode responder com `ok` OU `success`
    const ok = data.success === true || data.ok === true;

    if (!ok) {
      const msg = data.erro || data.error || 'Falha ao salvar';
      console.warn('[ImovelPersistenceService] Backend negou:', msg);
      return { success: false, error: msg };
    }

    return {
      success: true,
      imovel_uuid: data.imovel_uuid || data.uuid || data.imovel?.id || null,
      imovel_id:   data.imovel_id   || data.id   || null,
      acao:        data.acao,
      _etapa_atual: data._etapa_atual || imovel._etapa_atual,
    };
  } catch (err) {
    const msg = err?.response?.data?.erro || err?.message || 'Erro de comunicação';
    console.error('[ImovelPersistenceService] Erro HTTP:', msg);
    return { success: false, error: msg };
  }
};

export const carregarEstadoSoberano = async (imovelUuid) => {
  if (!imovelUuid) return { success: false, error: 'UUID obrigatório.' };

  try {
    const resp = await axios.get(
      `/api/imovel/carregar.php?uuid=${encodeURIComponent(imovelUuid)}`,
      { withCredentials: true }
    );
    const data = resp.data || {};

    const ok = data.success === true || data.ok === true;
    if (!ok) {
      return { success: false, error: data.erro || 'Falha ao carregar' };
    }

    return {
      success: true,
      imovel: data.imovel || data.dados,
      fonte:  data.fonte,
      _etapa_atual: (data.imovel || data.dados)?._etapa_atual || null,
    };
  } catch (err) {
    const msg = err?.response?.data?.erro || err?.message || 'Erro de comunicação';
    return { success: false, error: msg };
  }
};